import { useMemo } from 'react'
import { useList } from '@refinedev/core'

/**
 * The eight `feature_flags` rows that make up Jaguar-Owned's admin controls
 * (Task C1's migration `20260923121200_jaguar_owned_feature_switches.sql`
 * plus the pre-existing hub/listings/ask rows, Task C5's migration
 * `20260923121300_jaguar_owned_trust_switches.sql` adding vouches and
 * notes, and Task S1's migration `20261005120000_jaguar_owned_admin_preview.sql`
 * adding `jaguar_owned_admins_only`). This is the ONE place the key list is
 * written down — `JAGUAR_OWNED_FLAG_KEYS` and `isJaguarOwnedOwned` are
 * re-exported from `./page` for call sites that already import the page,
 * following the `isOnboardingOwned` / `isDemoOwned` precedent
 * (`../onboarding_settings/page.tsx`, `../demo_settings/page.tsx`).
 *
 * `jaguar_owned_admins_only` is NOT a `CONTROLS_CATALOG` row of its own —
 * Task S3 folds it into the hub row's three-state control (Off · Admins
 * only · Everyone) instead of giving it a switch. It's listed here purely
 * so (a) this hook's server-side `in` filter fetches it alongside the other
 * seven and (b) `isJaguarOwnedOwned` keeps hiding it from the generic
 * Feature Flags list, exactly like every other Jaguar-Owned row.
 */
export const JAGUAR_OWNED_FLAG_KEYS = [
  'jaguar_owned_enabled',
  'jaguar_owned_admins_only',
  'jaguar_owned_listings_open',
  'jaguar_owned_spotlight_enabled',
  'jaguar_owned_perks_enabled',
  'jaguar_owned_ask_enabled',
  'jaguar_owned_vouches_enabled',
  'jaguar_owned_notes_enabled',
] as const

export type JaguarOwnedFlagKey = (typeof JAGUAR_OWNED_FLAG_KEYS)[number]

export const isJaguarOwnedOwned = (key: string): boolean =>
  (JAGUAR_OWNED_FLAG_KEYS as readonly string[]).includes(key)

export type ControlKind = 'master' | 'independent' | 'part'

export interface ControlCatalogEntry {
  key: JaguarOwnedFlagKey
  title: string
  kind: ControlKind
  /** One line: what this part covers / what members see. */
  note: string
  /** Used verbatim in the turn-off confirm dialog's content, ahead of the
   * fixed "Nothing is deleted, and you can turn it back on at any time." */
  offImpact: string
}

/**
 * Catalog copy is EXACT — task-C4-brief.md's table, verbatim, plus task-
 * P2C-brief.md's two additions and task-S3-brief.md's updated hub note.
 * Order is the page's render order too: hub (master), listings
 * (independent), then the five hub-composed parts (spotlight, perks, ask,
 * vouches, notes).
 *
 * The hub row still carries ONE catalog entry — `kind: 'master'` — even
 * though it no longer renders as a boolean Switch (Task S3): its `note`
 * and `offImpact` are still the exact copy used by the three-state
 * control's row and its "Off" confirm dialog (`page.tsx`'s
 * `applyHubAudienceChange`). `jaguar_owned_admins_only`, the row the third
 * state writes, has no catalog entry of its own — see the comment on
 * `JAGUAR_OWNED_FLAG_KEYS` above.
 */
export const CONTROLS_CATALOG: ControlCatalogEntry[] = [
  {
    key: 'jaguar_owned_enabled',
    title: 'Jaguar-Owned hub',
    kind: 'master',
    note:
      'The Business tile, directory, business pages and every part below except New listings. "Admins only" shows the finished hub to admins while members see nothing.',
    offImpact: 'Members stop seeing Jaguar-Owned right away. Owners keep their own listing.',
  },
  {
    key: 'jaguar_owned_listings_open',
    title: 'New listings',
    kind: 'independent',
    note: 'Verified alumni can list a business from My Business. Works while the hub is off, so founding owners can list before launch.',
    offImpact: "Members can't start new listings or upload photos. Existing listings stay.",
  },
  {
    key: 'jaguar_owned_spotlight_enabled',
    title: 'Business of the Month',
    kind: 'part',
    note: "Spotlight tab, Home and feed cards, nominations, and owners' Business of the Month badge and report.",
    offImpact:
      'Members and owners stop seeing spotlights and nominations right away. You can still score and schedule spotlights here.',
  },
  {
    key: 'jaguar_owned_perks_enabled',
    title: 'Member perks',
    kind: 'part',
    note: "Perks tab, perk badges and filter, perk cards and passes, and the listing editor's perk step.",
    offImpact:
      "Members stop seeing perks right away, and Business of the Month stops requiring a perk. Perks aren't deleted.",
  },
  {
    key: 'jaguar_owned_ask_enabled',
    title: 'Ask the Jaguars',
    kind: 'part',
    note: 'The Ask tab, requests, replies and routing.',
    offImpact: 'Members stop seeing Ask the Jaguars right away. Existing requests are kept.',
  },
  {
    key: 'jaguar_owned_vouches_enabled',
    title: 'Vouches',
    kind: 'part',
    note: 'Vouch lists and counts on listings, and the "Vouch for this business" composer.',
    offImpact:
      "Members and owners stop seeing vouches right away, and Business of the Month stops requiring three. Vouches aren't deleted.",
  },
  {
    key: 'jaguar_owned_notes_enabled',
    title: 'Private notes',
    kind: 'part',
    note: 'The "Send a note" button on a listing, the note composer, and the owner\'s notes inbox.',
    offImpact: "Members can't send notes and owners' inboxes are hidden. Every note already received is kept.",
  },
]

export interface FeatureFlagRow {
  key: string
  enabled: boolean
  description: string | null
  updated_at: string
}

export interface UseJaguarOwnedSwitchesResult {
  /** The known Jaguar-Owned rows that exist today — a missing key just has no entry. */
  rows: FeatureFlagRow[]
  /** Keyed lookup, e.g. `byKey.get('jaguar_owned_enabled')?.enabled`. */
  byKey: Map<string, FeatureFlagRow>
  /** True only for the very first fetch, before any data has ever arrived. */
  isLoading: boolean
  /** A REFETCH in flight (including the background one after `refetch()`) —
   * separate from `isLoading`, which is only true before the first load.
   * The controls page uses this to show the Retry button's own spinner. */
  isFetching: boolean
  /** True only when there is NO data to fall back on — i.e. the first load
   * itself failed. A background refetch failure (focus refetch, or the one
   * after a write) does NOT flip this back on: every consumer keeps
   * rendering the last successfully fetched rows instead of hiding them or
   * guessing, per the "stale-while-erroring" rule below. */
  isError: boolean
  /** Awaitable — callers that need to know the fetch has actually landed
   * before releasing UI state (the controls page's "pending" switch) await
   * this rather than firing-and-forgetting `query.refetch()`. Passes
   * `cancelRefetch: false` so one row's post-write refetch doesn't cancel
   * another row's already-in-flight refetch (they share this one query) —
   * TanStack Query instead coalesces overlapping refetches into the
   * request that's already running. */
  refetch: () => Promise<void>
}

export interface UseJaguarOwnedSwitchesOptions {
  /**
   * Passed straight through to TanStack Query's `queryOptions.retry`.
   * Omit it (the default) to get the app-wide default retry behaviour
   * (`App.tsx`) — the right choice for a read-only console reader that has
   * no manual "Retry" UI of its own (the Business of the Month console, its
   * Eligibility tab, the Spotlight form, the listings page): without a
   * retry, one transient failure leaves those surfaces silently wrong until
   * a window-focus refetch happens to fire (M3).
   *
   * The controls page is the one caller that passes `false` on purpose — it
   * has its own manual "Retry" button (`jo-controls-retry`), and a silent
   * background retry there would fight that button's own loading state.
   */
  retry?: boolean | number
}

/**
 * Reads the eight Jaguar-Owned `feature_flags` rows (Task S3 added
 * `jaguar_owned_admins_only` to `JAGUAR_OWNED_FLAG_KEYS`, above). Every admin
 * surface that needs to know whether a part is on (the controls page, the
 * Business of the Month console banner, the Eligibility tab, the Spotlight
 * form, the listing page) reuses this hook rather than re-querying
 * `feature_flags` itself, so they all read the exact same rows the same way
 * — including the Business of the Month banner's own admins-only check.
 *
 * The `in` filter runs on the SERVER (PostgREST via the Supabase data
 * provider — the same `operator: 'in'` pattern used throughout the admin,
 * e.g. `member_badges/list.tsx`), so at most eight rows ever come back; there
 * is no client-side re-filtering of a wider page.
 *
 * `refetchOnWindowFocus: 'always'` overrides the app-wide default (off, with
 * a 5-minute staleTime — see App.tsx) so another admin's flip shows up on
 * the next tab-back-in rather than waiting out the staleTime window.
 */
export function useJaguarOwnedSwitches(options: UseJaguarOwnedSwitchesOptions = {}): UseJaguarOwnedSwitchesResult {
  const { result, query } = useList<FeatureFlagRow>({
    resource: 'feature_flags',
    filters: [{ field: 'key', operator: 'in', value: JAGUAR_OWNED_FLAG_KEYS }],
    pagination: { pageSize: JAGUAR_OWNED_FLAG_KEYS.length },
    meta: { idColumnName: 'key' },
    // `retry` defaults to the app-wide setting unless a caller opts out
    // (the controls page passes `false` for its own manual "Retry" button —
    // see `UseJaguarOwnedSwitchesOptions.retry` above). `refetchOnWindowFocus`
    // is unconditional for every caller.
    queryOptions:
      options.retry === undefined
        ? { refetchOnWindowFocus: 'always' }
        : { refetchOnWindowFocus: 'always', retry: options.retry },
  })

  const rows = result?.data ?? []
  const byKey = useMemo(() => new Map(rows.map((r) => [r.key, r])), [rows])
  // `query.data` (the RAW TanStack Query value, not Refine's `result`, which
  // always falls back to an empty array) is what tells apart "never loaded"
  // from "loaded once, and a later refetch failed" — TanStack keeps the last
  // successful `data` in place through a failed background refetch, only
  // flipping `isError`/`error`. Folding `!hasData` into `isError` here means
  // every consumer of this hook gets "stale while erroring" for free.
  const hasData = query.data !== undefined

  return {
    rows,
    byKey,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError && !hasData,
    refetch: async () => {
      await query.refetch({ cancelRefetch: false })
    },
  }
}
